import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFile, faDollarSign, faCalendarAlt, faCheckCircle, 
  faGavel, faAlignLeft, faExchangeAlt 
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import classNames from 'classnames';
import Timeline from 'components/base/Timeline';
import { Movimiento } from 'data/project-management/Movimiento';
import { format } from 'date-fns';
import { Row, Col } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';

interface ActivityTimelineProps {
  data: Movimiento[];
  // Hacemos que onItemClick sea opcional para no romper otros componentes
  onItemClick?: (item: Movimiento) => void;
}

const safeFormatDate = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return 'Fecha no disponible';

  let date: Date;

  if (typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else {
    date = dateInput;
  }

  if (isNaN(date.getTime())) {
    console.warn('Invalid date input:', dateInput);
    return 'Fecha inválida';
  }

  return format(date, 'dd/MM/yyyy HH:mm');
};

const getClaseIcon = (clase?: string) => {
  switch (clase?.toLowerCase()) {
    case 'archivo': return faFile;
    case 'cobro': return faDollarSign; // <-- CAMBIO AQUÍ
    case 'evento': return faCalendarAlt;
    case 'hecho': return faCheckCircle;
    case 'proveido': return faGavel;
    case 'texto': return faAlignLeft;
    case 'transferencia': return faExchangeAlt;
    case 'whatsapp': return faWhatsapp;
    default: return faAlignLeft;
  }
};

const MovimientosTimeline = ({ data, onItemClick }: ActivityTimelineProps) => {
  const timelineContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (timelineContainerRef.current) {
      timelineContainerRef.current.scrollTop = timelineContainerRef.current.scrollHeight;
    }
  }, [data]); 

  return (
    <div 
      ref={timelineContainerRef} 
      className="overflow-auto" 
      style={{ maxHeight: '500px', scrollBehavior: 'smooth' }}
    >
      <Timeline variant="vertical">
        {data.map((item, index) => {
          const isCobro = item?.descripcion?.substring(0, 5) === 'Cobro';

          // Extraemos el contenido para no duplicar código en el renderizado
          const timelineContent = (
            <Timeline.Content>
              <h5 className="fs-9 lh-sm">{item.descripcion}</h5>
              <p className="fs-9">{safeFormatDate(item.fecha)}</p>
              <p
                className={classNames('fs-9 text-body-secondary', {
                  'mb-5': index !== data.length - 1,
                  'mb-0': index === data.length - 1
                })}
              ></p>
            </Timeline.Content>
          );

          return (
            <Timeline.Item className="position-relative" key={item.id}>
              <Row className="g-md-3">
                <Col xs={12} md="auto" className="d-flex">
                  <Timeline.OppositeContent>
                    <p className="fs-10 fw-semibold text-body-tertiary text-opacity-85 text-end">
                      <br className="d-none d-md-block" />{' '}
                    </p>
                  </Timeline.OppositeContent>
                  <Timeline.Separator className="position-md-relative">
                    {/* Quitamos el style de Timeline.Dot y lo pasamos a un div interno */}
                    <Timeline.Dot className="bg-transparent p-0 border-0">
                      <div 
                        className="bg-primary-subtle d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                      >
                        <FontAwesomeIcon icon={getClaseIcon(item.clase_de_mto)} className="text-primary fs-9" />
                      </div>
                    </Timeline.Dot>
                    {index !== data.length - 1 && (
                      <Timeline.Bar className="h-100 border-dashed" />
                    )}
                  </Timeline.Separator>
                </Col>
                <Col>
                  {/* Comportamiento dinámico: Modal o Navegación clásica */}
                  {onItemClick ? (
                    <div
                      onClick={() => onItemClick(item)}
                      style={{ cursor: 'pointer' }}
                      className="d-block text-decoration-none"
                    >
                      {timelineContent}
                    </div>
                  ) : (
                    <NavLink
                      to={
                        isCobro
                          ? `/app/apps/project-management/cobro-detail/${item.clase_fk}`
                          : `/app/apps/project-management/movimiento/${item.id}`
                      }
                    >
                      {timelineContent}
                    </NavLink>
                  )}
                </Col>
              </Row>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </div>
  );
};

export default MovimientosTimeline;